/**
 * Classify customer types for public online booking lists so member-hosted types
 * (e.g. "Member Guest") never appear as selectable rates for anonymous guest bookers.
 *
 * Shared by `apps/booking` (UI filters) and `apps/api` (guest-slot persistence rules).
 */

export function customerTypeIsMemberFacing(ct: {
  category?: string | null;
  name?: string | null;
}): boolean {
  const name = String(ct.name ?? '').toLowerCase();
  if (/\bnon-?member\b/.test(name)) return false;
  const cat = String(ct.category ?? '')
    .toLowerCase()
    .trim();
  if (cat === 'member') return true;
  if (/\bmember\b/.test(name)) return true;
  return false;
}

/**
 * True when a booked player's CRM type should show their real name on member-facing
 * tee sheets (still subject to `showNameOnTeeSheet`).
 *
 * Broader than {@link customerTypeIsMemberFacing}: club rate types like senior, student,
 * or "Resident" often use categories/names that are not "member" but are not anonymous
 * public guests either.
 */
export function customerTypeShowsIndividualNameOnTeeSheet(ct: {
  category?: string | null;
  name?: string | null;
}): boolean {
  if (customerTypeIsMemberFacing(ct)) return true;
  const name = String(ct.name ?? '').toLowerCase();
  if (/\bnon-?member\b/.test(name)) return false;
  const cat = String(ct.category ?? '').toLowerCase().trim();
  if (cat === 'corporate' || cat === 'junior' || cat === 'senior' || cat === 'student') {
    return true;
  }
  if (/\bresident\b/.test(name)) return true;
  return false;
}

export function customerTypeIsPublicGuestOnlineBooking(ct: {
  category?: string | null;
  name?: string | null;
}): boolean {
  if (customerTypeIsMemberFacing(ct)) return false;
  const cat = String(ct.category ?? '')
    .toLowerCase()
    .trim();
  if (cat === 'guest') return true;
  if (/\bguest\b/.test(String(ct.name ?? '').toLowerCase())) return true;
  return false;
}

/** Whether this customer type may be used for online (public booking) flows. */
export function customerTypeCanBookOnline(ct: {
  canBookOnline?: boolean | null;
  status?: string | null;
}): boolean {
  const status = String(ct.status ?? 'active').toLowerCase().trim();
  if (status === 'inactive' || status === 'deleted') return false;
  return ct.canBookOnline !== false;
}

/**
 * Whether a reservation is being created through the public online booking channel
 * (as opposed to staff tee sheet / admin).
 */
export function isOnlineBookingReservationContext(source?: string | null, channel?: string | null): boolean {
  if (String(source ?? '').toLowerCase() === 'online') return true;
  const ch = String(channel ?? '').toLowerCase();
  return ch.includes('online') || ch.includes('booking');
}

/**
 * Whether a logged-in booking customer should see other players' names on tee cards / tee sheet.
 * Excludes public-guest-style types; includes category `member`, Resident/Senior paths, and
 * unknown profiles (prefer showing when `/me` did not include type details).
 */
export function customerCanViewTeeSheetPlayerNames(customer: unknown): boolean {
  if (!customer || typeof customer !== 'object') return false;
  const c = customer as Record<string, unknown>;
  const coarse = c.customerType;
  if (typeof coarse === 'string' && coarse.toLowerCase().trim() === 'member') return true;

  const shape = extractCustomerTypeShapeFromBookingProfile(c);
  if (!shape) return true;
  if (customerTypeIsMemberFacing(shape)) return true;
  return !customerTypeIsPublicGuestOnlineBooking(shape);
}

function extractCustomerTypeShapeFromBookingProfile(customer: Record<string, unknown>): {
  category?: string | null;
  name?: string | null;
} | null {
  const tid = customer.customerTypeId;
  if (tid && typeof tid === 'object' && tid !== null) {
    const o = tid as Record<string, unknown>;
    return {
      category: typeof o.category === 'string' ? o.category : null,
      name: typeof o.name === 'string' ? o.name : null,
    };
  }
  const ct = customer.customerType;
  if (ct && typeof ct === 'object' && ct !== null) {
    const o = ct as Record<string, unknown>;
    return {
      category: typeof o.category === 'string' ? o.category : null,
      name: typeof o.name === 'string' ? o.name : null,
    };
  }
  if (typeof ct === 'string') {
    return { name: ct, category: null };
  }
  return null;
}

/** When restrictToPublicOnlineGuestTypes is true, only types allowed for anonymous/public bookers. */
export function guestCustomerTypeAllowedForGuestSlot(
  ct: {
    category?: string | null;
    name?: string | null;
    canBookOnline?: boolean | null;
    status?: string | null;
  },
  opts: { restrictToPublicOnlineGuestTypes: boolean; enforceCanBookOnline?: boolean }
): boolean {
  if (opts.enforceCanBookOnline !== false && !customerTypeCanBookOnline(ct)) return false;
  if (!opts.restrictToPublicOnlineGuestTypes) return true;
  return customerTypeIsPublicGuestOnlineBooking(ct);
}
